/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { ComponentStore } from '@ngrx/component-store';
import {
    BehaviorSubject,
    Observable,
    Subscription,
} from 'rxjs';
import { DevLoggerService } from 'dev-logger-service';
import {
    ILogListResponseData,
    TLog,
    TLogIdentifier,
    TLogListStateParams,
} from '../logs.types';
import { LogsApiRequestService } from './logs-api-request.service';
import { ILogsStates, LogsStore } from './logs.store';

/**
 * @param hasError - Error state from API.
 * @param logs - List of logs returned by API.
 */
export interface ILogListStates {
    hasError: boolean;
    logs: TLog[];
}

/**
 * @abstract
 * @description
 *     An abstract store for state and effect management of log list components.
 *     This store should never be used as an direct injectable except the abstract"log-list.component".
 *     It should be inherited by more concrete log list component stores.
 * @author Akul Aggarwal, Alex Klyuev, Zhiqian Liu
 */
export abstract class LogListStore<T extends ILogListStates> extends ComponentStore<T> {
    // ********************************** Selectors **********************************

    public readonly hasError$ = this.select(({ hasError }) => hasError);

    public readonly logList$ = this.select(({ logs }) => logs);

    /**
     * The log that's been most recently inspected by the log cinematic view.
     * Set to be a behavior subject since this can always be of use in a c-view opened after its
     * latest value has been emitted.
     */
    public recentlyViewedLog$ = new BehaviorSubject<TLog>(null);

    /**
     * Within the current page, the index of the log most recently inspected by the log cinematic
     * view. Emit -1 when no logs have been inspected yet.
     * Set to be a behavior subject since this can always be of use in a c-view opened after its
     * latest value has been emitted.
     */
    public readonly recentlyViewedLogIndex$ = new BehaviorSubject<number>(-1);

    // ****************************** Abstract Selectors ******************************

    /**
     * The logs that have been highlighted in the table.
     * Need to be overridden by a concrete store.
     */
    public abstract selectedLogRows$: Observable<TLog[]>;

    // ******************************* Abstract Properties *****************************

    /**
     * The request ID for the log list request. Should be overridden by the concrete store.
     */
    protected abstract logListRequestId: string;

    // ********************************** Constructor ***********************************

    constructor(
        initialState: T,
        protected readonly logsStore: LogsStore<ILogsStates>,
        protected readonly logsApiRequestService: LogsApiRequestService,
        protected readonly devLoggerService: DevLoggerService,
    ) {
        super(initialState);
    }

    // ********************************** Methods **********************************

    /**
     * Clear error and timeout states and set loading states.
     */
    protected startLoading = (): void => {
        this.setErrorState(false);
        this.logsStore.setIsTimedOut(false);
        this.logsStore.setisErrorBannerEnabled(true);
        this.logsStore.setisTimeoutBannerEnabled(true);
        this.logsStore.setTableIsLoading(true);
        this.logsStore.setIsTableApiLoopInProgress(true);
    };

    /**
     * Set isLoading to false.
     */
    protected stopLoading = (): void => {
        this.logsStore.setTableIsLoading(false);
    };

    /**
     * Update state with logs once data is returned from API.
     */
    protected handleApiData = (data: ILogListResponseData): void => {
        this.setLogs(data.results);
        this.logsStore.setListLogCount(data.count);
    };

    /**
     * Handle API errors.
     */
    protected handleApiError = (err: any): void => {
        this.setLogs([]);
        this.devLoggerService.error(err);
        this.logsStore.setListLogCount(0);
        this.setErrorState(true);
        this.stopLoading();
        this.logsStore.setIsTableApiLoopInProgress(false);
    };

    /**
     * Set API loop state upon loop completion.
     */
    protected handleApiLoopComplete = (isTimedOut: boolean): void => {
        this.logsStore.setIsTableApiLoopInProgress(false);

        if (isTimedOut) {
            this.logsStore.setIsTimedOut(true);
        }
    };

    /**
     * Set error state on local component store and module global store
     * (for component-level and page-level error messages).
     */
    protected setErrorState = (hasError: boolean): void => {
        this.logsStore.setHasError(hasError);
        this.setState(state => ({
            ...state,
            hasError,
        }));
    };

    // ********************************** Updaters **********************************

    /**
     * Update the state with the logs data.
     */
    private readonly setLogs = (logList: TLog[]): void => {
        this.setState(state => ({
            ...state,
            logs: logList,
        }));
    };

    // ******************************* Abstract Effects *******************************

    /**
     * Call logs API for log list data until all data is returned or timeout.
     * Upon each API return, it updates the state and makes a new request if needed.
     * Should be overridden by a concrete class.
     */
    public abstract getLogList(apiParams: TLogListStateParams): void;

    /**
     * Since the getLogList call can be chained with exponentially increasing timeout,
     * This method should be called to cancel any ongoing log list request; usually upon component destroy.
     * Should be overridden by a concrete class.
     */
    public abstract cancelLogListRequest(): void;

    /**
     * Refresh list when under a custom timeframe. Usually need the implemented getLogList.
     * Need to be overridden by a concrete store.
     */
    public abstract refreshList(): void;

    /**
     * Download one or more logs by log ID(s).
     * Should be overridden by a concrete class.
     */
    public abstract downloadLogs(observableOrValue: TLogIdentifier[] | Observable<TLogIdentifier[]>): Subscription;

    /**
     * Download all logs on the current page.
     * Should be overridden by a concrete class.
     */
    public abstract downloadCurrentPage(observableOrValue?: void | Observable<void>): Subscription;

    /**
     * Download the logs selected by the user in the table.
     * Should be overridden by a concrete class.
     */
    public abstract downloadSelectedLogs(observableOrValue?: void | Observable<void>): Subscription;
}
